/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import {
  ChevronDown,
  ChevronUp,
  Download,
  Edit,
  Trash2,
  Phone,
  Mail,
  MoreHorizontal,
  Star,
  History,
  CheckCircle,
  AlertCircle,
  Clock,
  UserCheck,
  FileText,
  MapPin,
  Key,
  Briefcase,
  Wallet,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Pagination from "../../components/Pagination";
import { VETTING_STAGE_LABELS } from "../../utils/constants";
import placeholderImage from "../../assets/placeholder.webp";

const CaregiverTable = ({
  caregivers = [],
  pagination = null,
  isLoading = false,
  showVettingColumn = true,
  showWorkingColumn = false,
  showUploadButton = false,
  onUploadClick,
  onAddClick,
  onPageChange,
  onItemsPerPageChange,
  onToggleStatus,
  onDeleteClick,
  onResetPasswordClick,
  onViewVettingHistory,
  onViewWorkingDetails,
  onViewWorkHistory,
  onViewBankDetails,
  onDownloadCV,
  downloadingCV = null,
  currentPage = 1,
  itemsPerPage = 5,
  expandedRow = null,
  onToggleExpand,
  activeMenu = null,
  onSetActiveMenu,
  getVettingColor = () => "",
  getVettingIcon = () => null,
  formatCategory = (cat) => cat,
  formatDate = (date) => date,
}) => {
  const navigate = useNavigate();
  const [localExpandedRow, setLocalExpandedRow] = useState(expandedRow);
  const [localActiveMenu, setLocalActiveMenu] = useState(activeMenu);

  // Use local state if not controlled by parent
  useEffect(() => {
    if (expandedRow === undefined) {
      setLocalExpandedRow(null);
    }
  }, [caregivers]);

  useEffect(() => {
    if (activeMenu === undefined) {
      setLocalActiveMenu(null);
    }
  }, []);

  const currentExpandedRow =
    expandedRow !== undefined ? expandedRow : localExpandedRow;
  const currentActiveMenu =
    activeMenu !== undefined ? activeMenu : localActiveMenu;

  const handleToggleExpand = (id) => {
    const newExpanded = currentExpandedRow === id ? null : id;
    if (onToggleExpand) {
      onToggleExpand(newExpanded);
    } else {
      setLocalExpandedRow(newExpanded);
    }
  };

  const handleSetActiveMenu = (id) => {
    const newActive = currentActiveMenu === id ? null : id;
    if (onSetActiveMenu) {
      onSetActiveMenu(newActive);
    } else {
      setLocalActiveMenu(newActive);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest(".action-menu")) return;
      handleSetActiveMenu(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleToggleStatus = (id, field) => {
    if (onToggleStatus) {
      onToggleStatus(id, field);
    }
  };

  const handleDeleteClick = (id, name) => {
    if (onDeleteClick) {
      onDeleteClick(id, name);
    }
    handleSetActiveMenu(null);
  };

  const handleResetPasswordClick = (caregiver) => {
    if (onResetPasswordClick) {
      onResetPasswordClick(caregiver);
    }
    handleSetActiveMenu(null);
  };

  const handleViewVettingHistory = (caregiverId) => {
    if (onViewVettingHistory) {
      onViewVettingHistory(caregiverId);
    }
    handleSetActiveMenu(null);
  };

  const handleViewBankDetails = (caregiverId) => {
    if (onViewBankDetails) {
      onViewBankDetails(caregiverId);
    }
    handleSetActiveMenu(null);
  };

  const handleViewWorkingDetails = (caregiverId) => {
    if (onViewWorkingDetails) {
      onViewWorkingDetails(caregiverId);
    }
    handleSetActiveMenu(null);
  };

  const handleViewWorkHistory = (caregiverId) => {
    if (onViewWorkHistory) {
      onViewWorkHistory(caregiverId);
    }
    handleSetActiveMenu(null);
  };

  const handleDownloadCV = (caregiverId, caregiverName) => {
    if (onDownloadCV) {
      onDownloadCV(caregiverId, caregiverName);
    }
    handleSetActiveMenu(null);
  };

  const handleEditClick = (caregiverId) => {
    // Get current path to use as return path
    const currentPath = window.location.pathname;
    console.log(
      "CaregiverTable - Navigating to edit with return path:",
      currentPath,
    );
    // Pass return path as both state AND query parameter for reliability
    navigate(
      `/caregivers/edit/${caregiverId}?returnTo=${encodeURIComponent(currentPath)}`,
      {
        state: { returnPath: currentPath },
      },
    );
    handleSetActiveMenu(null);
  };

  // Default vetting color function
  const defaultGetVettingColor = (status) => {
    switch (status) {
      case "Active":
      case "Onboarded":
        return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800";
      case "Re_Examination":
      case "Rejected":
        return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800";
      case "Interview":
        return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-200 dark:border-blue-800";
      case "Document_Verification":
        return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800";
      case "Guarantee_And_Medical_Test":
        return "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border border-purple-200 dark:border-purple-800";
      case "Get_Exam":
        return "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800";
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600";
    }
  };

  // Default vetting icon function
  const defaultGetVettingIcon = (status) => {
    switch (status) {
      case "Active":
      case "Onboarded":
        return <CheckCircle size={16} className="text-emerald-500" />;
      case "Re_Examination":
      case "Rejected":
        return <AlertCircle size={16} className="text-red-500" />;
      case "Interview":
        return <UserCheck size={16} className="text-blue-500" />;
      case "Document_Verification":
        return <FileText size={16} className="text-yellow-500" />;
      default:
        return <Clock size={16} className="text-gray-500" />;
    }
  };

  const finalGetVettingColor = getVettingColor || defaultGetVettingColor;
  const finalGetVettingIcon = getVettingIcon || defaultGetVettingIcon;
  const finalFormatCategory = formatCategory || ((cat) => cat);
  const finalFormatDate = formatDate || ((date) => date);

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  // Get avatar color based on name
  const getAvatarColor = (name) => {
    if (!name) return "bg-gray-500";
    const colors = [
      "bg-blue-500",
      "bg-green-500",
      "bg-yellow-500",
      "bg-red-500",
      "bg-purple-500",
      "bg-pink-500",
      "bg-indigo-500",
      "bg-teal-500",
      "bg-orange-500",
      "bg-cyan-500",
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border overflow-visible">
      <div className="overflow-x-auto min-h-[400px] relative">
        {isLoading && (
          <div className="absolute inset-0 bg-white/50 dark:bg-black/50 flex items-center justify-center z-10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        )}
        <table className="w-full text-sm text-left min-w-[900px]">
          <thead className="text-xs text-gray-500 dark:text-dark-muted uppercase bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th className="px-4 sm:px-6 py-4 font-bold tracking-wider">
                Caregiver
              </th>
              <th className="px-4 sm:px-6 py-4 font-bold tracking-wider">
                Contact & Category
              </th>
              <th className="px-4 sm:px-6 py-4 font-bold tracking-wider">
                Fee & Rating
              </th>
              {showVettingColumn && (
                <th className="px-4 sm:px-6 py-4 font-bold tracking-wider">
                  Vetting
                </th>
              )}
              {showWorkingColumn && (
                <th className="px-4 sm:px-6 py-4 font-bold tracking-wider text-center">
                  Working Status
                </th>
              )}
              <th className="px-4 sm:px-6 py-4 font-bold tracking-wider text-center">
                Availability
              </th>
              <th className="px-4 sm:px-6 py-4 font-bold tracking-wider text-center">
                Account Status
              </th>
              <th className="px-4 sm:px-6 py-4 font-bold tracking-wider text-center">
                Actions
              </th>
              <th className="px-4 sm:px-6 py-4 font-bold tracking-wider"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {caregivers.length > 0 ? (
              caregivers.map((c) => (
                <React.Fragment key={c.id}>
                  <tr
                    className={`hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors group ${
                      currentExpandedRow === c.id
                        ? "bg-gray-50 dark:bg-gray-800/50"
                        : ""
                    }`}
                  >
                    <td className="px-4 sm:px-6 py-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={c.avatar || placeholderImage}
                          alt=""
                          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-card dark:border-dark-surface shadow-sm"
                          onError={(e) => {
                            e.target.src = placeholderImage;
                          }}
                        />
                        <div>
                          <p className="font-bold text-text dark:text-dark-text">
                            {c.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-dark-muted">
                            {c.id}
                          </p>
                          {c.subcity && (
                            <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mt-1">
                              <MapPin size={10} />
                              <span>{c.subcity}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-dark-muted">
                          <Phone size={12} /> {c.phone}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                          <Mail size={12} />{" "}
                          <span className="truncate max-w-[150px]">
                            {c.email}
                          </span>
                        </div>
                        <span className="inline-flex mt-1 px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 dark:bg-gray-700 text-text dark:text-dark-text w-fit">
                          {finalFormatCategory(c.category)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4">
                      <div className="font-bold text-text dark:text-dark-text">
                        Br {c.fees}
                      </div>
                      <div className="flex items-center gap-1 text-yellow-500 text-xs mt-1">
                        <Star size={12} fill="currentColor" />{" "}
                        {c.rating.toFixed(1)}
                      </div>
                    </td>
                    {showVettingColumn && (
                      <td className="px-4 sm:px-6 py-4">
                        <div className="flex flex-col gap-2">
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-bold ${finalGetVettingColor(
                              c.vettingStatus,
                            )}`}
                          >
                            {VETTING_STAGE_LABELS[c.vettingStatus] ||
                              c.vettingStatus}
                          </span>
                          {onViewVettingHistory && (
                            <button
                              onClick={() => handleViewVettingHistory(c.id)}
                              className="text-xs text-primary hover:underline flex items-center gap-1"
                            >
                              <History size={12} /> View History
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                    {showWorkingColumn && (
                      <td className="px-4 sm:px-6 py-4 text-center">
                        {c.isWorking ? (
                          <button
                            onClick={() => handleViewWorkingDetails(c.id)}
                            className="px-3 py-1 text-xs font-bold rounded-full transition-colors cursor-pointer bg-emerald-600 text-white dark:bg-emerald-700 dark:text-white hover:bg-emerald-700 dark:hover:bg-emerald-800"
                          >
                            Working
                          </button>
                        ) : (
                          <span className="px-3 py-1 text-xs font-bold rounded-full bg-gray-500 text-white dark:bg-gray-600 dark:text-white">
                            Not Working
                          </span>
                        )}
                      </td>
                    )}
                    {/* Availability Column */}
                    <td className="px-4 sm:px-6 py-4 text-center">
                      <button
                        onClick={() => handleToggleStatus(c.id, "isAvailable")}
                        className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${
                          c.isAvailable
                            ? "bg-primary"
                            : "bg-gray-300 dark:bg-gray-600"
                        }`}
                      >
                        <span className="sr-only">Toggle availability</span>
                        <span
                          className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform duration-200 ${
                            c.isAvailable ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                      <div className="text-[10px] font-medium text-gray-500 dark:text-dark-muted mt-1">
                        {c.isAvailable ? "Available" : "Busy"}
                      </div>
                    </td>
                    {/* Account Status Column */}
                    <td className="px-4 sm:px-6 py-4 text-center">
                      <button
                        onClick={() => handleToggleStatus(c.id, "isActive")}
                        className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          c.isActive
                            ? "bg-blue-600"
                            : "bg-gray-300 dark:bg-gray-600"
                        }`}
                      >
                        <span className="sr-only">Toggle account status</span>
                        <span
                          className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform duration-200 ${
                            c.isActive ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                      <div className="text-[10px] font-medium text-gray-500 dark:text-dark-muted mt-1">
                        {c.isActive ? "Active" : "Inactive"}
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-center relative action-menu">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSetActiveMenu(c.id);
                        }}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-400 hover:text-primary dark:hover:text-primary transition-colors"
                      >
                        <MoreHorizontal size={18} />
                      </button>

                      {currentActiveMenu === c.id && (
                        <div className="absolute right-8 top-8 w-48 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                          <button
                            onClick={() => handleEditClick(c.id)}
                            className="w-full text-left px-4 py-2.5 text-sm text-text dark:text-dark-text hover:bg-accent dark:hover:bg-gray-700 flex items-center gap-2"
                          >
                            <Edit size={16} /> Edit Profile
                          </button>
                          <button
                            onClick={() => handleViewBankDetails(c.id)}
                            className="w-full text-left px-4 py-2.5 text-sm text-text dark:text-dark-text hover:bg-accent dark:hover:bg-gray-700 flex items-center gap-2"
                          >
                            <Wallet size={16} /> Bank Details
                          </button>
                          {onDownloadCV && (
                            <button
                              onClick={() => handleDownloadCV(c.id, c.name)}
                              disabled={downloadingCV === c.id}
                              className={`w-full text-left px-4 py-2.5 text-sm text-text dark:text-dark-text hover:bg-accent dark:hover:bg-gray-700 flex items-center gap-2 ${
                                downloadingCV === c.id
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                            >
                              {downloadingCV === c.id ? (
                                <>
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary mr-1"></div>
                                  Downloading...
                                </>
                              ) : (
                                <>
                                  <Download size={16} /> Download CV
                                </>
                              )}
                            </button>
                          )}
                          {showWorkingColumn &&
                            c.isWorking &&
                            onViewWorkingDetails && (
                              <button
                                onClick={() => handleViewWorkingDetails(c.id)}
                                className="w-full text-left px-4 py-2.5 text-sm text-text dark:text-dark-text hover:bg-accent dark:hover:bg-gray-700 flex items-center gap-2"
                              >
                                <Briefcase size={16} /> View Working Details
                              </button>
                            )}
                          {onViewVettingHistory && (
                            <button
                              onClick={() => handleViewVettingHistory(c.id)}
                              className="w-full text-left px-4 py-2.5 text-sm text-text dark:text-dark-text hover:bg-accent dark:hover:bg-gray-700 flex items-center gap-2"
                            >
                              <History size={16} /> Vetting History
                            </button>
                          )}
                          {onViewWorkHistory && (
                            <button
                              onClick={() => handleViewWorkHistory(c.id)}
                              className="w-full text-left px-4 py-2.5 text-sm text-text dark:text-dark-text hover:bg-accent dark:hover:bg-gray-700 flex items-center gap-2"
                            >
                              <Briefcase size={16} /> Work History
                            </button>
                          )}
                          {onResetPasswordClick && (
                            <button
                              onClick={() => handleResetPasswordClick(c)}
                              className="w-full text-left px-4 py-2.5 text-sm text-text dark:text-dark-text hover:bg-accent dark:hover:bg-gray-700 flex items-center gap-2"
                            >
                              <Key size={16} /> Reset Password
                            </button>
                          )}
                          {onDeleteClick && (
                            <>
                              <div className="h-px bg-gray-100 dark:bg-gray-700 my-1"></div>
                              <button
                                onClick={() => handleDeleteClick(c.id, c.name)}
                                className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                              >
                                <Trash2 size={16} /> Delete User
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-right">
                      <button
                        onClick={() => handleToggleExpand(c.id)}
                        className="text-gray-400 hover:text-primary dark:hover:text-primary"
                      >
                        {currentExpandedRow === c.id ? (
                          <ChevronUp size={20} />
                        ) : (
                          <ChevronDown size={20} />
                        )}
                      </button>
                    </td>
                  </tr>
                  {currentExpandedRow === c.id && (
                    <tr>
                      <td
                        colSpan={
                          8 +
                          (showVettingColumn ? 1 : 0) +
                          (showWorkingColumn ? 1 : 0)
                        }
                        className="px-0"
                      >
                        <div className="bg-gray-50 dark:bg-gray-900/30 p-4 sm:p-6 border-y border-gray-100 dark:border-dark-border">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 text-sm">
                            <div className="space-y-3">
                              <h4 className="font-bold text-text dark:text-dark-text uppercase text-xs tracking-wide border-b border-gray-200 dark:border-gray-700 pb-1">
                                Professional Details
                              </h4>
                              <div className="space-y-2">
                                <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                                  <span className="text-gray-500 dark:text-dark-muted min-w-[100px] sm:min-w-[120px]">
                                    Education:
                                  </span>
                                  <span className="font-medium text-text dark:text-dark-text flex-1">
                                    {c.education}
                                  </span>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                                  <span className="text-gray-500 dark:text-dark-muted min-w-[100px] sm:min-w-[120px]">
                                    Experience:
                                  </span>
                                  <span className="font-medium text-text dark:text-dark-text flex-1">
                                    {c.experience}
                                  </span>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                                  <span className="text-gray-500 dark:text-dark-muted min-w-[100px] sm:min-w-[120px]">
                                    Nationality:
                                  </span>
                                  <span className="font-medium text-text dark:text-dark-text flex-1">
                                    {c.nationality}
                                  </span>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                                  <span className="text-gray-500 dark:text-dark-muted min-w-[100px] sm:min-w-[120px]">
                                    Joined:
                                  </span>
                                  <span className="font-medium text-text dark:text-dark-text flex-1">
                                    {finalFormatDate(c.createdAt)}
                                  </span>
                                </div>
                                {c.subcity && (
                                  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                                    <span className="text-gray-500 dark:text-dark-muted min-w-[100px] sm:min-w-[120px]">
                                      Subcity:
                                    </span>
                                    <span className="font-medium text-text dark:text-dark-text flex-1 flex items-center gap-1">
                                      <MapPin size={12} />
                                      {c.subcity}
                                    </span>
                                  </div>
                                )}
                                {c.addressLine1 && (
                                  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                                    <span className="text-gray-500 dark:text-dark-muted min-w-[100px] sm:min-w-[120px]">
                                      Address Line 1:
                                    </span>
                                    <span className="font-medium text-text dark:text-dark-text flex-1">
                                      {c.addressLine1}
                                    </span>
                                  </div>
                                )}
                                {c.addressLine2 && (
                                  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                                    <span className="text-gray-500 dark:text-dark-muted min-w-[100px] sm:min-w-[120px]">
                                      Address Line 2:
                                    </span>
                                    <span className="font-medium text-text dark:text-dark-text flex-1">
                                      {c.addressLine2}
                                    </span>
                                  </div>
                                )}
                                {(c.hasCourse ||
                                  c.courseDetails ||
                                  c.courseDetails_amh) && (
                                  <>
                                    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                                      <span className="text-gray-500 dark:text-dark-muted min-w-[100px] sm:min-w-[120px]">
                                        Certificate:
                                      </span>
                                      <span className="font-medium text-text dark:text-dark-text flex-1">
                                        {c.hasCourse ? "Yes" : "No"}
                                      </span>
                                    </div>
                                    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                                      <span className="text-gray-500 dark:text-dark-muted min-w-[100px] sm:min-w-[120px]">
                                        Certificate Name:
                                      </span>
                                      <span className="font-medium text-text dark:text-dark-text flex-1">
                                        {c.courseDetails ||
                                          c.courseDetails_amh ||
                                          "Not specified"}
                                      </span>
                                    </div>
                                  </>
                                )}
                                {(c.hasReferee ||
                                  c.refereeName ||
                                  c.refereePhone) && (
                                  <>
                                    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                                      <span className="text-gray-500 dark:text-dark-muted min-w-[100px] sm:min-w-[120px]">
                                        Referee Name:
                                      </span>
                                      <span className="font-medium text-text dark:text-dark-text flex-1">
                                        {c.refereeName || "Not specified"}
                                      </span>
                                    </div>
                                    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                                      <span className="text-gray-500 dark:text-dark-muted min-w-[100px] sm:min-w-[120px]">
                                        Referee Phone:
                                      </span>
                                      <span className="font-medium text-text dark:text-dark-text flex-1 flex items-center gap-1">
                                        <Phone size={12} />
                                        {c.refereePhone || "Not specified"}
                                      </span>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>

                            <div className="space-y-3">
                              <h4 className="font-bold text-text dark:text-dark-text uppercase text-xs tracking-wide border-b border-gray-200 dark:border-gray-700 pb-1">
                                Skills & Preferences
                              </h4>
                              <div>
                                <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2 mb-2">
                                  <span className="text-gray-500 dark:text-dark-muted min-w-[100px] sm:min-w-[120px]">
                                    Languages:
                                  </span>
                                  <div className="flex flex-wrap gap-2 flex-1">
                                    {Array.isArray(c.languages) ? (
                                      c.languages.map((l) => (
                                        <span
                                          key={l}
                                          className="px-2 py-0.5 bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded text-xs font-medium text-text dark:text-dark-text"
                                        >
                                          {l}
                                        </span>
                                      ))
                                    ) : (
                                      <span className="text-gray-500 dark:text-dark-muted">
                                        {c.languages || "Not specified"}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                                  <span className="text-gray-500 dark:text-dark-muted min-w-[100px] sm:min-w-[120px]">
                                    Work Preference:
                                  </span>
                                  <span className="font-medium text-text dark:text-dark-text flex-1">
                                    {c.workPreferences}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="space-y-3">
                              <h4 className="font-bold text-text dark:text-dark-text uppercase text-xs tracking-wide border-b border-gray-200 dark:border-gray-700 pb-1">
                                About
                              </h4>
                              <p className="text-gray-600 dark:text-dark-muted leading-relaxed">
                                {c.about}
                              </p>
                              {c.onboardedAt && (
                                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                                  <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                                    <CheckCircle size={16} />
                                    <span>
                                      Onboarded on{" "}
                                      {finalFormatDate(c.onboardedAt)}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td
                  colSpan={
                    8 +
                    (showVettingColumn ? 1 : 0) +
                    (showWorkingColumn ? 1 : 0)
                  }
                  className="px-6 py-12 text-center text-gray-500 dark:text-dark-muted"
                >
                  {isLoading ? "Loading..." : "No caregivers found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {pagination && pagination.totalPages > 0 && (
        <div className="border-t border-gray-100 dark:border-dark-border bg-gray-50/50 dark:bg-dark-surface p-2">
          <Pagination
            currentPage={currentPage}
            totalPages={pagination.totalPages}
            onPageChange={onPageChange}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={onItemsPerPageChange}
            totalItems={pagination.total}
          />
        </div>
      )}
    </div>
  );
};

export default CaregiverTable;
